import React, { useState, useEffect, useMemo } from 'react';
import { 
  Timeline, 
  Card, 
  Typography, 
  Tag, 
  Space, 
  Avatar, 
  Empty, 
  Input, 
  Select, 
  Button, 
  DatePicker, 
  Row, 
  Col, 
  Badge,
  Tooltip,
  Divider
} from 'antd';
import { 
  CalendarOutlined, 
  FileTextOutlined, 
  PlusOutlined, 
  EyeOutlined, 
  LikeOutlined, 
  SearchOutlined,
  FilterOutlined,
  ClearOutlined,
  TagOutlined,
  StarOutlined,
  UserOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';

const { Title, Text } = Typography;
const { Option } = Select;

// 获取用户的文章数据
const getUserPosts = () => {
  const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
  const userPosts = JSON.parse(localStorage.getItem(`userPosts_${currentUser.id}`) || '[]');
  return userPosts;
};

// 获取所有用户的数据（管理员专用）
const getAllUsersPosts = () => {
  const allPosts = [];
  const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
  
  // 如果不是管理员，只获取自己的
  if (!currentUser.isAdmin) {
    return getUserPosts();
  }
  
  // 管理员获取所有用户的文章
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    
    if (key.startsWith('userPosts_')) {
      const userId = key.replace('userPosts_', '');
      const userPosts = JSON.parse(localStorage.getItem(key) || '[]');
      
      // 获取用户信息
      let userInfo = { username: '未知用户', avatar: '👤', isAdmin: false };
      
      const mockUsers = [
        { id: 1, username: 'admin', avatar: '👑', isAdmin: true },
        { id: 2, username: 'user1', avatar: '😊', isAdmin: false },
        { id: 3, username: 'test', avatar: '🌟', isAdmin: false },
      ];
      
      const mockUser = mockUsers.find(u => u.id.toString() === userId);
      if (mockUser) {
        userInfo = mockUser;
      }
      
      const postsWithUserInfo = userPosts.map(post => ({
        ...post,
        userId: parseInt(userId),
        author: post.author || {
          id: parseInt(userId),
          username: userInfo.username,
          avatar: userInfo.avatar,
          isAdmin: userInfo.isAdmin
        }
      }));
      
      allPosts.push(...postsWithUserInfo);
    }
  }
  
  return allPosts.sort((a, b) => {
    const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
    const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
    return dateB - dateA;
  });
};

const Archive = () => {
  const [archiveData, setArchiveData] = useState([]);
  const [totalViews, setTotalViews] = useState(0);
  const [totalLikes, setTotalLikes] = useState(0);
  const [allPosts, setAllPosts] = useState([]);
  const [currentUser, setCurrentUser] = useState({});
  
  // 筛选状态
  const [searchText, setSearchText] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [dateRange, setDateRange] = useState([]);
  const [selectedUser, setSelectedUser] = useState('all');
  const [sortBy, setSortBy] = useState('date-desc');
  
  // 获取所有标签
  const allTags = useMemo(() => {
    const tagsSet = new Set();
    allPosts.forEach(post => {
      if (post.tags && post.tags.length > 0) {
        post.tags.forEach(tag => tagsSet.add(tag));
      }
    });
    return Array.from(tagsSet).sort();
  }, [allPosts]);
  
  // 获取所有用户（管理员用）
  const allUsers = useMemo(() => {
    const usersMap = new Map();
    allPosts.forEach(post => {
      if (post.author) {
        const key = `${post.author.id}-${post.author.username}`;
        if (!usersMap.has(key)) {
          usersMap.set(key, {
            id: post.author.id,
            username: post.author.username,
            avatar: post.author.avatar,
            isAdmin: post.author.isAdmin,
            count: 0
          });
        }
        usersMap.get(key).count++;
      }
    });
    return Array.from(usersMap.values());
  }, [allPosts]);

  useEffect(() => {
    // 获取当前用户
    const user = JSON.parse(localStorage.getItem('currentUser') || '{}');
    setCurrentUser(user);
    
    // 获取所有文章
    const posts = user.isAdmin ? getAllUsersPosts() : getUserPosts();
    setAllPosts(posts);
    
    // 计算总浏览量和点赞数
    let views = 0;
    let likes = 0;
    
    posts.forEach(post => {
      views += post.views || 0;
      likes += post.likes || 0;
    });
    
    setTotalViews(views);
    setTotalLikes(likes);
    
    // 初始归档数据
    processAndSetArchiveData(posts);
  }, []);

  // 处理并设置归档数据
  const processAndSetArchiveData = (posts) => {
    // 按年份和月份分组
    const groupedByYear = {};
    
    posts.forEach(post => {
      if (post.createdAt) {
        const date = new Date(post.createdAt);
        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        const monthStr = `${month.toString().padStart(2, '0')}月`;
        
        if (!groupedByYear[year]) {
          groupedByYear[year] = {};
        }
        
        if (!groupedByYear[year][month]) {
          groupedByYear[year][month] = {
            month: monthStr,
            posts: []
          };
        }
        
        groupedByYear[year][month].posts.push({
          id: post.id,
          title: post.title,
          date: post.createdAt,
          tags: post.tags || [],
          views: post.views || 0,
          likes: post.likes || 0,
          author: post.author,
          userId: post.userId
        });
      }
    });
    
    // 转换为数组格式
    const archiveArray = Object.keys(groupedByYear).map(year => {
      const months = Object.values(groupedByYear[year]).sort((a, b) => {
        return parseInt(b.month) - parseInt(a.month);
      });
      
      return {
        year: `${year}年`,
        months: months
      };
    }).sort((a, b) => parseInt(b.year) - parseInt(a.year));
    
    setArchiveData(archiveArray);
  };

  // 处理筛选
  const handleFilter = () => {
    let filteredPosts = [...allPosts];
    
    // 搜索文本筛选（标题和内容）
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filteredPosts = filteredPosts.filter(post => 
        (post.title && post.title.toLowerCase().includes(searchLower)) ||
        (post.content && post.content.toLowerCase().includes(searchLower))
      );
    }
    
    // 标签筛选
    if (selectedTags.length > 0) {
      filteredPosts = filteredPosts.filter(post => 
        post.tags && selectedTags.some(tag => post.tags.includes(tag))
      );
    }
    
    // 时间范围筛选
    if (dateRange && dateRange.length === 2) {
      const [start, end] = dateRange;
      filteredPosts = filteredPosts.filter(post => {
        if (!post.createdAt) return false;
        const postDate = new Date(post.createdAt);
        return postDate >= start && postDate <= end;
      });
    }
    
    // 用户筛选（仅管理员）
    if (currentUser.isAdmin && selectedUser !== 'all') {
      const userId = parseInt(selectedUser);
      filteredPosts = filteredPosts.filter(post => post.userId === userId);
    }
    
    // 排序
    filteredPosts.sort((a, b) => {
      const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
      const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
      
      switch (sortBy) {
        case 'date-asc':
          return dateA - dateB;
        case 'views-desc':
          return (b.views || 0) - (a.views || 0);
        case 'likes-desc':
          return (b.likes || 0) - (a.likes || 0);
        case 'title-asc':
          return (a.title || '').localeCompare(b.title || '');
        case 'date-desc':
        default:
          return dateB - dateA;
      }
    });
    
    // 重新处理归档数据
    processAndSetArchiveData(filteredPosts);
    
    // 更新统计
    let views = 0;
    let likes = 0;
    filteredPosts.forEach(post => {
      views += post.views || 0;
      likes += post.likes || 0;
    });
    setTotalViews(views);
    setTotalLikes(likes);
  };

  // 重置筛选
  const handleReset = () => {
    setSearchText('');
    setSelectedTags([]);
    setDateRange([]);
    setSelectedUser('all');
    setSortBy('date-desc');
    
    processAndSetArchiveData(allPosts);
    
    let views = 0;
    let likes = 0;
    allPosts.forEach(post => {
      views += post.views || 0;
      likes += post.likes || 0;
    });
    setTotalViews(views);
    setTotalLikes(likes);
  };

  // 当筛选条件变化时自动应用
  useEffect(() => {
    // 添加防抖，避免频繁更新
    const timer = setTimeout(() => {
      handleFilter();
    }, 300);
    
    return () => clearTimeout(timer);
  }, );

  const getFilteredPostCount = () => {
    let count = 0;
    archiveData.forEach(year => {
      year.months.forEach(month => {
        count += month.posts.length;
      });
    });
    return count;
  };

  // 准备时间线数据
  const timelineItems = useMemo(() => {
    const items = [];
    
    archiveData.forEach(yearData => {
      // 添加年份项
      items.push({
        children: (
          <Card size="small" style={{ 
            background: '#fff9fb', 
            borderColor: '#ffd6e7',
            borderRadius: '12px'
          }}>
            <Title level={3} style={{ margin: 0, color: '#ff7ba9' }}>
              {yearData.year}
            </Title>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              ({yearData.months.length}个月，{yearData.months.reduce((sum, month) => sum + month.posts.length, 0)}篇日记)
            </Text>
          </Card>
        ),
        color: 'pink',
        dot: <CalendarOutlined style={{ fontSize: '20px', color: '#ff9ebb' }} />
      });
      
      // 添加月份项
      yearData.months.forEach(monthData => {
        items.push({
          children: (
            <div style={{ paddingLeft: 20 }}>
              <div style={{ marginBottom: 12 }}>
                <Text strong style={{ fontSize: 16, color: '#ff7ba9' }}>
                  {monthData.month}
                </Text>
                <Text type="secondary" style={{ marginLeft: 8, fontSize: '13px' }}>
                  ({monthData.posts.length}篇)
                </Text>
              </div>
              
              <div>
                {monthData.posts.map(post => (
                  <div key={post.id} style={{ marginBottom: 12 }}>
                    <Card 
                      size="small" 
                      style={{ 
                        width: '100%', 
                        borderColor: '#ffd6e7',
                        cursor: 'pointer',
                        borderRadius: '12px',
                        transition: 'all 0.3s'
                      }}
                      hoverable
                      onClick={() => window.location.href = `/post/${post.id}`}
                    >
                      <Space direction="vertical" size={10} style={{ width: '100%' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Space>
                            <FileTextOutlined style={{ color: '#ff9ebb' }} />
                            <Text strong style={{ fontSize: 14 }}>
                              {post.title}
                            </Text>
                          </Space>
                          
                          {currentUser.isAdmin && post.author && (
                            <Tooltip title={`作者: ${post.author.username}`}>
                              <Avatar 
                                size="small" 
                                style={{ 
                                  backgroundColor: post.author.isAdmin ? '#fff9db' : '#fde3cf',
                                  border: post.author.isAdmin ? '1px solid gold' : 'none'
                                }}
                              >
                                {post.author.avatar}
                              </Avatar>
                            </Tooltip>
                          )}
                        </div>
                        
                        <Space size={4}>
                          <CalendarOutlined style={{ fontSize: 12 }} />
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {new Date(post.date).toLocaleDateString()}
                          </Text>
                        </Space>
                        
                        <Space size={12}>
                          <Space size={2}>
                            <EyeOutlined style={{ fontSize: 11, color: '#666' }} />
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              {post.views}
                            </Text>
                          </Space>
                          <Space size={2}>
                            <LikeOutlined style={{ fontSize: 11, color: '#666' }} />
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              {post.likes}
                            </Text>
                          </Space>
                        </Space>
                        
                        {post.tags && post.tags.length > 0 && (
                          <Space wrap>
                            {post.tags.map(tag => (
                              <Tag 
                                key={tag} 
                                color="pink"
                                style={{ 
                                  fontSize: 11, 
                                  padding: '0 8px', 
                                  margin: 0,
                                  borderRadius: '10px'
                                }}
                              >
                                {tag}
                              </Tag>
                            ))}
                          </Space>
                        )}
                      </Space>
                    </Card>
                  </div>
                ))}
              </div>
            </div>
          ),
          color: 'pink'
        });
      });
    });
    
    // 添加开始记录项
    items.push({
      children: (
        <Card size="small" style={{ 
          background: '#fff9fb', 
          borderColor: '#ffd6e7',
          borderRadius: '12px'
        }}>
          <Text type="secondary" style={{ 
            textAlign: 'center', 
            display: 'block',
            fontSize: '13px'
          }}>
            从这里开始记录 📝
          </Text>
        </Card>
      ),
      dot: <div style={{ 
        width: 12, 
        height: 12, 
        borderRadius: '50%', 
        background: '#ff9ebb',
        boxShadow: '0 0 0 3px rgba(255, 158, 187, 0.2)'
      }} />
    });
    
    return items;
  }, [archiveData, currentUser.isAdmin]);

  if (allPosts.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '50px 20px' }}>
        <Empty
          description={
            <span>
              还没有日记，开始记录你的生活吧！
            </span>
          }
        >
          <Link to="/write">
            <Button type="primary" icon={<PlusOutlined />}>
              写第一篇日记
            </Button>
          </Link>
        </Empty>
      </div>
    );
  }

  return (
    <div>
      <Title level={2} style={{ textAlign: 'center', marginBottom: 30 }}>
        日记归档
        {currentUser.isAdmin && (
          <Badge 
            count="管理员" 
            style={{ 
              backgroundColor: '#ff9ebb', 
              marginLeft: 10,
              fontSize: '12px',
              fontWeight: 'normal'
            }} 
          />
        )}
      </Title>
      
      {/* 筛选面板 */}
      <Card 
        style={{ 
          marginBottom: 24, 
          background: 'linear-gradient(135deg, #fff5f9, #ffe6f0)',
          border: '1px solid #ffd1e0',
          borderRadius: '16px'
        }}
      >
        <div className="archive-search">
          <Space direction="vertical" size={20} style={{ width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <FilterOutlined style={{ color: '#ff9ebb', fontSize: '18px' }} />
              <Text strong style={{ fontSize: '16px' }}>筛选与搜索</Text>
            </div>
            
            <Row gutter={[16, 16]}>
              <Col xs={24} md={12} lg={6}>
                <Input.Search
                  placeholder="搜索标题或内容..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onSearch={handleFilter}
                  enterButton={<SearchOutlined />}
                  size="large"
                  allowClear
                  style={{ width: '100%' }}
                />
              </Col>
              
              <Col xs={24} md={12} lg={6}>
                <Select
                  mode="multiple"
                  placeholder="选择标签"
                  value={selectedTags}
                  onChange={setSelectedTags}
                  size="large"
                  style={{ width: '100%' }}
                  allowClear
                  maxTagCount={2}
                  suffixIcon={<TagOutlined />}
                  popupStyle={{ padding: '8px' }}
                >
                  {allTags.map(tag => (
                    <Option key={tag} value={tag}>
                      {tag}
                    </Option>
                  ))}
                </Select>
              </Col>
              
              <Col xs={24} md={12} lg={6}>
                <DatePicker.RangePicker
                  value={dateRange}
                  onChange={(dates) => setDateRange(dates)}
                  size="large"
                  style={{ width: '100%' }}
                  placeholder={['开始日期', '结束日期']}
                  format="YYYY-MM-DD"
                  allowClear
                />
              </Col>
              
              <Col xs={24} md={12} lg={6}>
                <Select
                  placeholder="排序方式"
                  value={sortBy}
                  onChange={setSortBy}
                  size="large"
                  style={{ width: '100%' }}
                  suffixIcon={<StarOutlined />}
                >
                  <Option value="date-desc">最新在前</Option>
                  <Option value="date-asc">最早在前</Option>
                  <Option value="views-desc">浏览最多</Option>
                  <Option value="likes-desc">点赞最多</Option>
                  <Option value="title-asc">标题 A-Z</Option>
                </Select>
              </Col>
            </Row>
            
            {currentUser.isAdmin && (
              <Row gutter={[16, 16]}>
                <Col xs={24} md={12} lg={6}>
                  <Select
                    placeholder="筛选用户"
                    value={selectedUser}
                    onChange={setSelectedUser}
                    size="large"
                    style={{ width: '100%' }}
                    allowClear
                    suffixIcon={<UserOutlined />}
                  >
                    <Option value="all">所有用户</Option>
                    {allUsers.map(user => (
                      <Option key={user.id} value={user.id}>
                        <Space>
                          <Avatar size="small">
                            {user.avatar}
                          </Avatar>
                          <span>
                            {user.username}
                            {user.isAdmin && ' 👑'}
                          </span>
                          <Text type="secondary">({user.count}篇)</Text>
                        </Space>
                      </Option>
                    ))}
                  </Select>
                </Col>
              </Row>
            )}
            
            <Divider style={{ margin: '8px 0' }} />
            
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text type="secondary" style={{ fontSize: '14px' }}>
                找到 <Text strong style={{ color: '#ff9ebb' }}>{getFilteredPostCount()}</Text> 篇日记
                {getFilteredPostCount() !== allPosts.length && (
                  <Text type="secondary"> (共 {allPosts.length} 篇)</Text>
                )}
              </Text>
              
              <Button 
                icon={<ClearOutlined />} 
                onClick={handleReset}
                size="middle"
                style={{ 
                  borderRadius: '12px',
                  padding: '6px 16px'
                }}
              >
                重置筛选
              </Button>
            </div>
          </Space>
        </div>
      </Card>
      
      {/* 标签云（快速筛选） */}
      {allTags.length > 0 && (
        <Card style={{ marginBottom: 24 }}>
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <TagOutlined style={{ color: '#ff9ebb' }} />
              <Text strong style={{ fontSize: '15px' }}>热门标签</Text>
              <Text type="secondary" style={{ fontSize: '13px' }}>(点击快速筛选)</Text>
            </div>
            
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {allTags.map(tag => {
                const isSelected = selectedTags.includes(tag);
                return (
                  <Tag
                    key={tag}
                    style={{ 
                      cursor: 'pointer',
                      fontSize: '14px',
                      padding: '6px 14px',
                      borderRadius: '16px',
                      background: isSelected ? '#ff9ebb' : 'rgba(255, 158, 187, 0.1)',
                      borderColor: isSelected ? '#ff9ebb' : '#ffd1e0',
                      color: isSelected ? 'white' : '#ff7ba9',
                      transition: 'all 0.3s',
                      margin: 0
                    }}
                    onClick={() => {
                      if (isSelected) {
                        setSelectedTags(selectedTags.filter(t => t !== tag));
                      } else {
                        setSelectedTags([...selectedTags, tag]);
                      }
                    }}
                  >
                    {tag} {isSelected && '✓'}
                  </Tag>
                );
              })}
              
              {selectedTags.length > 0 && (
                <Button 
                  type="link" 
                  size="small" 
                  onClick={() => setSelectedTags([])}
                  style={{ 
                    padding: '6px 8px',
                    fontSize: '13px'
                  }}
                >
                  清除选中
                </Button>
              )}
            </div>
          </Space>
        </Card>
      )}
      
      <Card style={{ marginBottom: 24 }}>
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Title level={4} style={{ margin: 0, fontSize: '18px' }}>
            📊 统计信息
          </Title>
          <Row gutter={[16, 16]} style={{ textAlign: 'center' }}>
            <Col xs={12} md={4}>
              <div style={{ padding: '12px' }}>
                <div style={{ fontSize: 24, fontWeight: 'bold', color: '#ff9ebb' }}>
                  {archiveData.length}
                </div>
                <Text type="secondary" style={{ fontSize: '13px' }}>记录年份</Text>
              </div>
            </Col>
            <Col xs={12} md={4}>
              <div style={{ padding: '12px' }}>
                <div style={{ fontSize: 24, fontWeight: 'bold', color: '#ff9ebb' }}>
                  {getFilteredPostCount()}
                </div>
                <Text type="secondary" style={{ fontSize: '13px' }}>筛选结果</Text>
              </div>
            </Col>
            <Col xs={12} md={4}>
              <div style={{ padding: '12px' }}>
                <div style={{ fontSize: 24, fontWeight: 'bold', color: '#ff9ebb' }}>
                  {totalViews}
                </div>
                <Text type="secondary" style={{ fontSize: '13px' }}>总浏览量</Text>
              </div>
            </Col>
            <Col xs={12} md={4}>
              <div style={{ padding: '12px' }}>
                <div style={{ fontSize: 24, fontWeight: 'bold', color: '#ff9ebb' }}>
                  {totalLikes}
                </div>
                <Text type="secondary" style={{ fontSize: '13px' }}>总点赞数</Text>
              </div>
            </Col>
            <Col xs={24} md={4}>
              <div style={{ padding: '12px' }}>
                <div style={{ fontSize: 24, fontWeight: 'bold', color: '#ff9ebb' }}>
                  {archiveData.length > 0 ? Math.round(getFilteredPostCount() / archiveData.length) : 0}
                </div>
                <Text type="secondary" style={{ fontSize: '13px' }}>年平均</Text>
              </div>
            </Col>
          </Row>
        </Space>
      </Card>
      
      {archiveData.length === 0 ? (
        <Card style={{ textAlign: 'center', padding: '60px 20px', borderRadius: '16px' }}>
          <Empty
            description={
              <Text type="secondary" style={{ fontSize: '15px' }}>
                没有找到符合条件的日记
              </Text>
            }
          />
          <Button 
            type="primary" 
            onClick={handleReset}
            style={{ 
              marginTop: 20,
              borderRadius: '12px',
              padding: '8px 24px'
            }}
          >
            重置筛选条件
          </Button>
        </Card>
      ) : (
        <Timeline
          mode="alternate"
          items={timelineItems}
        />
      )}
    </div>
  );
};

export default Archive;